import NossoNumero from '@core/components/NossoNumero';
import { SimpleTable } from '@core/components/Table';
import { TableActionButton, TableActionButtonDelete, TableActionButtonEdit } from '@core/components/TableAction';
import TableActionContainer from '@core/components/TableAction/TableActionContainer';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { permissaoUtils } from '@core/util/permissaoUtil';
import { Competencia, DataBR } from '@format/DATA';
import { Tooltip, makeStyles } from '@material-ui/core';
import CancelarBaixaIcon from 'app/assets/icon/cancelarBaixaIcon';
import NfIcon from 'app/assets/icon/nfIcon';
import PartnerBankIcon from 'app/assets/icon/partnerBankIcon';
import PixIcon from 'app/assets/icon/pixIcon';
import contaReceberApi from 'app/services/api/contaReceberApi';
import { fechamentoPeriodoApi } from 'app/services/api/fechamentoPeriodoApi';
import { relatorioApi } from 'app/services/api/relatorioApi';
import * as Actions from 'app/store/actions';
import { eventReactGA } from 'app/utils/analyticsUtil';
import { visualizarRelatorioV2 } from 'app/utils/enviromentUtil';
import { getCurrency } from 'app/utils/monetarioUtil';
import moment from 'moment';
import React, { useContext } from 'react';
import { FaBarcode, FaHandHoldingUsd } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { ContaReceberContext } from '../contaReceberContext';
import ContaReceberFab from './ContaReceberFab';
import { contaReceberModais } from './modal/contaReceberModais';

const contaReceberStyles = makeStyles({
  situacaoContainer: {
    display: 'inline-block',
  },
  badgeRetencao: {
    color: '#ffffff',
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 2,
    paddingBottom: 2,
    fontSize: 12,
    fontWeight: 600,
    borderRadius: '0px !important',
    width: '20px',
    backgroundColor: '#3286a8',
    float: 'left',
  },
  badge: {
    color: '#ffffff',
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 2,
    paddingBottom: 2,
    fontSize: 12,
    fontWeight: 600,
    borderRadius: '0px !important',
    width: '60px',
    textAlign: 'center',
  },
  badgeAberta: {
    backgroundColor: '#3286a8',
  },
  badgeBaixada: {
    backgroundColor: '#2dc44d',
  },
  badgeExcluida: {
    backgroundColor: '#b53838',
  },
  badgeRecebidoParcialmente: {
    backgroundColor: '#e65c17',
  },
  buttonEditDisabled: {
    cursor: 'default',
    color: 'white',
    borderRadius: '30%',
    padding: 4,
    border: 0,
    backgroundColor: 'rgb(157, 157, 157, 0.8)',
    '&:hover': {
      backgroundColor: 'rgb(157, 157, 157, 1)',
      boxShadow: 'none',
      borderColor: 'rgb(157, 157, 157, 0)',
    },
    '&:active': {
      boxShadow: 'none',
      backgroundColor: 'rgb(157, 157, 157, 0.9)',
      borderColor: 'rgb(157, 157, 157, 0)',
    },
    '&:focus': {
      boxShadow: '0 0 0 0.2rem rgb(157, 157, 157, 0.9)',
      borderColor: 'rgb(157, 157, 157, 0)',
    },
  },
});

const ContaReceberTable = ({
  fetchContas,
  fetchConta,
  onDelete,
  onSelecaoChange,
  onTipoSelecaoChange,
  onCancelarBaixa,
  handleModalBaixar,
  onReenviarBoletoPartnerBank,
  onTour,
}) => {
  const styles = contaReceberStyles();

  const [loading] = useContext(ContaReceberContext).loading;
  const [contasReceber] = useContext(ContaReceberContext).contasReceber;
  const [selecao] = useContext(ContaReceberContext).selecao;
  const [totalElements] = useContext(ContaReceberContext).totalElements;
  const [search] = useContext(ContaReceberContext).search;
  const [tipoSelecao] = useContext(ContaReceberContext).tipoSelecao;
  const [somatorios] = useContext(ContaReceberContext).somatorios;
  const [, setModal] = useContext(ContaReceberContext).modal;
  const [sort, setSort] = useContext(ContaReceberContext).sort;
  const [page, setPage] = useContext(ContaReceberContext).page;
  const [size, setSize] = useContext(ContaReceberContext).size;
  const [filtroAvancado] = useContext(ContaReceberContext).filtroAvancado;
  const [, setContaReceberId] = useContext(ContaReceberContext).contaReceberId;

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };
  const showError = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'error' }));
  };

  const handleOpenVisualizarModal = async (contaReceber) => {
    await fetchConta(contaReceber?.id);

    setModal(contaReceberModais.visualizarContaReceber);
  };

  const handleEditar = async (contaReceber) => {
    const response = await fechamentoPeriodoApi.buscarUltimoPeriodo('RECEBIMENTOS_VENDAS', contaReceber.dataVencimento);
    if (response.data) {
      if (response.data.bloquearAnteriores) {
        if (
          moment(contaReceber.dataVencimento).isSameOrBefore(response.data.periodo) &&
          response.data.funcionalidades.includes('RECEBIMENTOS_VENDAS')
        ) {
          throw showError(
            'Não é possível lançar, editar e excluir contas com data de vencimento referente a um período já fechado no sistema.'
          );
        }
      }
    }

    await fetchConta(contaReceber?.id);

    setModal(contaReceberModais.editarContaReceber);
  };

  const handleExcluir = async (contaReceber) => {
    onDelete({ ids: [contaReceber], q: search, tipoSelecao: 'NENHUM' });
  };

  const handleBaixar = async (contaReceber) => {
    await fetchConta(contaReceber?.id);

    setModal(contaReceberModais.baixarContaReceber);
  };

  const handleVisualizarBoleto = async (contaReceber) => {
    if (contaReceber?.uuidBoleto) {
      const filtro = {
        ...filtroAvancado,
        ids: [contaReceber.id],
        query: search,
        tipoSelecao,
        tiposCobrancas: ['BOLETO', 'PARTNER_BANK'],
      };
      eventReactGA('Conta a receber', 'Visualizar boleto');
      const response = await relatorioApi().salvarFiltroBoleto(filtro);
      visualizarRelatorioV2(response.data);
      // visualizarRelatorio(contaReceber.uuidBoleto, 'conta-receber/boleto', 'titulo=boleto', 'protegerComSenha=false');
    }
  };

  const handleVisualizarErrosPartnerBank = (id) => {
    setContaReceberId(id);

    setModal(contaReceberModais.errosPartnerBank);
  };

  const handleReenviarBoleto = (id) => {
    onReenviarBoletoPartnerBank(id);
  };

  const getSituacaoTag = (situacao) => {
    if (situacao === 'ABERTA') {
      return (
        <div className={styles.situacaoContainer}>
          <div className={`${styles.badge} ${styles.badgeAberta}`}>Aberta</div>
        </div>
      );
    }
    if (situacao === 'BAIXADA') {
      return (
        <div className={styles.situacaoContainer}>
          <div className={`${styles.badge} ${styles.badgeBaixada}`}>Baixada</div>
        </div>
      );
    }
    if (situacao === 'EXCLUIDA') {
      return (
        <div className={styles.situacaoContainer}>
          <div className={`${styles.badge} ${styles.badgeExcluida}`}>Excluída</div>
        </div>
      );
    }
    if (situacao === 'PARCIAL') {
      return (
        <div className={styles.situacaoContainer}>
          <div className={`${styles.badge} ${styles.badgeRecebidoParcialmente}`}>Parcial</div>
        </div>
      );
    }
    return '-';
  };

  const getIconRetencao = () => {
    return (
      <Tooltip title="Cobrança possui retenção de impostos" placement="left">
        <div className={`${styles.badgeRetencao}`}>R</div>
      </Tooltip>
    );
  };

  const getValorContaReceber = (contaReceber) => {
    return (
      <div>
        {contaReceber?.possuiRetencao && getIconRetencao()}
        <div>{getCurrency(contaReceber?.valor)}</div>
      </div>
    );
  };

  const getSituacaoRemessa = (contaReceber) => {
    const excluido = contaReceber.situacao === 'EXCLUIDA';
    const baixado = contaReceber.situacao === 'BAIXADA';

    if (
      contaReceber.situacaoRemessa === 'NAO_TRATADA_BANCO' ||
      ((contaReceber.situacaoRemessa === 'PENDENTE' || contaReceber.situacaoRemessa === 'GERADA') &&
        (excluido || baixado))
    ) {
      return 'NAO_TRATADA_BANCO';
    }

    return contaReceber.situacaoRemessa;
  };

  const getSomatorioSelecao = () => {
    let total = 0;
    if (tipoSelecao === 'TODOS_FILTRO') {
      total = somatorios?.somatorioFiltro;
    } else if (selecao) {
      selecao.forEach((receita) => {
        total += receita.valor;
      });
    }

    return total;
  };

  const handleEnviarNF = async (contaReceber) => {
    eventReactGA('Conta a receber', 'Envia NF por e-mail');

    try {
      const resp = await contaReceberApi.enviarNotaFiscal(new Array(contaReceber.notaFiscalId));

      if (resp?.status === 200) {
        showSuccess('A Nota fiscal foi enviada com sucesso para o e-mail cadastrado para o cliente.');
      }
    } catch (error) {
      apiUtils.handleApiError(error);
    }
  };

  const footerTable = (
    <div>
      <div style={{ padding: '12px 0 0 24px' }}>
        Valor total das receitas (R$): {getCurrency(somatorios?.somatorioFiltro || 0).replace('R$', '')}
      </div>
      <div style={{ padding: '12px 0 0 24px' }}>
        Valor das receitas selecionadas (R$): {getCurrency(getSomatorioSelecao() || 0).replace('R$', '')}
      </div>
    </div>
  );

  const celulaRecebimento = (data) => {
    const dataPagamento = DataBR(data.dataPagamento);
    const dataLiquidacao = data.dataLiquidacao ? 'Liquidado em ' + DataBR(data.dataLiquidacao) : null;

    if (!dataLiquidacao) {
      return <span>{dataPagamento}</span>;
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          lineHeight: 1.1,
        }}
      >
        <span>{dataPagamento}</span>
        <span style={{ fontSize: '9px' }}>{dataLiquidacao}</span>
      </div>
    );
  };

  /**
   RNE-077 - Ordenação de contas a pagar e receber
   O sistema deve exibir, por padrão, os registros ordenados decrescentemente pela coluna “Vencimento”.
   
   RNE-143 - Exibição padrão do grid
   O sistema deve exibir no grid, como padrão, os registros com situação "Aberta" que vencem no mês atual, independente da competencia.
   */
  const columns = [
    {
      name: 'cliente.pessoa.nome',
      title: 'Cliente',
      align: 'left',
      getCellValue: (contaReceber) => contaReceber.nomeCliente,
      sortingEnabled: true,
    },

    {
      name: ' ',
      title: '',
      align: 'left',
      hideTooltip: true, // Implementar no tooltip código de transação do Pix
      width: 40,
      sortingEnabled: false,
      getCellValue: (data) => (
        <>
          <div>
            {data?.possuiPix && <PixIcon size="15" color="#5890aa" />}
            {data?.tipoCobranca === 'PARTNER_BANK' && <PartnerBankIcon size="20" />}
          </div>
        </>
      ),
    },

    {
      name: 'mesCompetencia',
      title: 'Competência',
      align: 'center',
      getCellValue: (data) => Competencia(data.mesCompetencia),
      hideTooltip: true,
      sortingEnabled: true,
    },
    {
      name: 'receitaCobranca.nossoNumero',
      title: 'Nosso número',
      align: 'center',
      getCellValue: (contaReceber) => {
        return (
          <NossoNumero
            id={contaReceber.id}
            className={'stepNossoNumero'}
            value={contaReceber?.nossoNumero || contaReceber?.nossoNumeroAntigo}
            situacao={getSituacaoRemessa(contaReceber)}
            type="CONTA_RECEBER"
            hasError={contaReceber?.situacaoRemessa === 'REJEITADA'}
            showIconError={
              contaReceber?.situacaoRemessa === 'REJEITADA' && contaReceber?.tipoCobranca === 'PARTNER_BANK'
            }
            onClickError={handleVisualizarErrosPartnerBank}
            tooltipError="Visualizar erros de registro"
            reenviar={contaReceber?.situacaoRemessa === 'PENDENTE' && contaReceber?.tipoCobranca === 'PARTNER_BANK'}
            onClickReenviar={handleReenviarBoleto}
            tooltipReenviar="Reenviar cobrança ao Partner Bank"
            reenviarDisabled={
              contaReceber?.situacaoRemessa === 'REENVIADA' && contaReceber?.tipoCobranca === 'PARTNER_BANK'
            }
            tooltipReenviarDisabled="A cobrança já foi enviada ao Partner Bank"
          />
        );
      },
      hideTooltip: true,
      sortingEnabled: true,
    },
    {
      name: 'dataVencimento',
      title: <div className="stepIconeOrdenacao">Vencimento</div>,
      align: 'center',
      getCellValue: (data) => data.dataVencimento && DataBR(data.dataVencimento),
      hideTooltip: true,
      sortingEnabled: true,
    },
    {
      name: 'dataRecebimento',
      title: 'Recebimento',
      align: 'center',
      getCellValue: (data) => celulaRecebimento(data),
      hideTooltip: true,
      sortingEnabled: true,
      width: 125,
    },
    {
      name: 'valorBruto',
      title: 'Valor (R$)',
      align: 'right',
      getTooltip: (contaReceber) => getCurrency(contaReceber?.valor),
      getCellValue: (contaReceber) => {
        return getValorContaReceber(contaReceber);
      },
      sortingEnabled: true,
    },
    {
      name: 'valorTotalRecebido',
      title: 'Valor recebido (R$)',
      align: 'right',
      getCellValue: (contaReceber) => {
        return contaReceber?.valorPago !== null && contaReceber?.valorPago !== undefined
          ? getCurrency(contaReceber?.valorPago)
          : undefined;
      },
      sortingEnabled: true,
      width: 150,
    },
    {
      name: 'situacao',
      title: 'Situação',
      align: 'right',
      getCellValue: (data) => getSituacaoTag(data.situacao),
      hideTooltip: true,
      sortingEnabled: true,
      width: 100,
    },
    {
      name: '',
      title: '',
      align: 'center',
      sortingEnabled: false,
      getCellValue: (contaReceber) => {
        // RNE-458 - Não permissão de edição para cobrança enviada para registro
        const disableEditButton =
          contaReceber?.situacaoRemessa === 'GERADA' && contaReceber.tipoCobranca !== 'PARTNER_BANK';

        const disableArquivoRetorno = contaReceber?.origemBaixa === 'ARQUIVO_RETORNO';
        const possuiVisualizacaoBoleto =
          contaReceber?.tipoCobranca === 'BOLETO' || contaReceber?.tipoCobranca === 'PARTNER_BANK';

        return (
          <TableActionContainer onTour={onTour}>
            {(contaReceber.situacao === 'ABERTA' || contaReceber.situacao === 'PARCIAL') &&
              permissaoUtils.contaReceber().editar && (
                <TableActionButtonEdit
                  className={'stepIconeEditar'}
                  onClick={() => {
                    if (!disableEditButton) {
                      handleEditar(contaReceber);
                    }
                  }}
                  title={
                    disableEditButton
                      ? 'Este boleto já foi incluído em uma remessa. Aguarde o retorno do banco.'
                      : 'Editar'
                  }
                  style={disableEditButton && styles.buttonEditDisabled}
                />
              )}
            {contaReceber.situacao === 'ABERTA' && permissaoUtils.contaReceber().excluir && (
              <TableActionButtonDelete onClick={() => handleExcluir(contaReceber)} className={'stepIconeExcluir'} />
            )}
            {(contaReceber.situacao === 'ABERTA' || contaReceber.situacao === 'PARCIAL') && !contaReceber.subJudice && (
              <TableActionButton
                title="Baixar"
                icon={<FaHandHoldingUsd size={10} className={'stepIconeBaixar'} />}
                onClick={() => {
                  handleBaixar(contaReceber);
                }}
              />
            )}

            {onTour && (
              <TableActionContainer onTour={onTour}>
                <TableActionButton
                  size={10}
                  title={
                    disableArquivoRetorno
                      ? 'Somente contas baixadas manualmente podem ter baixa cancelada.'
                      : 'Cancelar baixa'
                  }
                  icon={<CancelarBaixaIcon size={10} className={'stepIconeCancelarBaixa'} />}
                  style={disableArquivoRetorno && styles.buttonEditDisabled}
                />

                <TableActionButton
                  title="Enviar NF por e-mail"
                  size={10}
                  icon={<NfIcon size={10} className={'stepEnviarNF'} />}
                />
              </TableActionContainer>
            )}

            {(contaReceber.situacao === 'BAIXADA' || contaReceber.situacao === 'PARCIAL') &&
              permissaoUtils.contaReceber().editar && (
                <TableActionButton
                  size={10}
                  title={
                    disableArquivoRetorno
                      ? 'Somente contas baixadas manualmente podem ter baixa cancelada.'
                      : 'Cancelar baixa'
                  }
                  icon={<CancelarBaixaIcon size={10} />}
                  onClick={() => {
                    if (!disableArquivoRetorno) {
                      onCancelarBaixa(
                        { selecao: [contaReceber], tipoSelecao: 'NENHUM', params: { q: search } },
                        { page, q: search, size, sort }
                      );
                    }
                  }}
                  style={disableArquivoRetorno && styles.buttonEditDisabled}
                />
              )}

            <TableActionButton
              title={possuiVisualizacaoBoleto ? 'Visualizar boleto' : 'Cobrança não possui boleto'}
              icon={<FaBarcode size={10} className={'stepVisualizarBoleto'} />}
              style={!possuiVisualizacaoBoleto && styles.buttonEditDisabled}
              onClick={() => {
                if (possuiVisualizacaoBoleto) {
                  handleVisualizarBoleto(contaReceber);
                }
              }}
            />
            {/* }{(contaReceber?.tipoCobranca === 'BOLETO' || contaReceber?.tipoCobranca === 'PARTNER_BANK') && (
            )} */}
            {/* Falta implementar Relatório do Pix */}
            {/* {contaReceber?.tipoCobranca === 'PIX' && (
                        <TableActionButton
                          title="Pix"
                          size={10}
                          icon={<PixIcon size={10} />}
                          onClick={() => handleVisualizarPix(contaReceber)}
                        />
                      )} */}
            {/* Falta implementar Relatório de NF */}
            {contaReceber?.notaFiscalId && (
              <TableActionButton
                title="Enviar NF por e-mail"
                size={10}
                icon={<NfIcon size={10} />}
                onClick={() => handleEnviarNF(contaReceber)}
              />
            )}
          </TableActionContainer>
        );
      },
    },
  ];

  useReduxLoading(loading);

  const hiddenColumnNames = [];

  // RNE-301
  if (
    filtroAvancado.situacao !== 'BAIXADA' &&
    filtroAvancado.situacao !== 'PARCIAL' &&
    filtroAvancado.situacao !== 'TODAS'
  ) {
    hiddenColumnNames.push('dataRecebimento');
    hiddenColumnNames.push('valorRecebido');
  }

  return (
    <>
      <SimpleTable
        defaultSorting={[{ columnName: 'dataVencimento', direction: 'desc' }]}
        columns={columns}
        rows={contasReceber}
        selection={selecao}
        loading={loading}
        totalCount={totalElements}
        searchBy={search}
        fetchData={fetchContas}
        onRowClick={handleOpenVisualizarModal}
        onSelectionChange={onSelecaoChange}
        onCurrentPageChange={setPage}
        onPageSizeChange={setSize}
        onSortingChange={setSort}
        selectionType={tipoSelecao}
        onSelectionTypeChange={onTipoSelecaoChange}
        footerTable={footerTable}
        params={filtroAvancado}
        hiddenColumnNames={hiddenColumnNames}
        contemFiltroAvancado
      />
      {(permissaoUtils.contaReceber().cadastrar ||
        permissaoUtils.contaReceber().editar ||
        permissaoUtils.contaReceber().excluir) && (
        <ContaReceberFab
          selected={selecao}
          onDelete={onDelete}
          onCancelarBaixa={onCancelarBaixa}
          handleModalBaixar={handleModalBaixar}
          fetchConta={fetchConta}
        />
      )}
    </>
  );
};

export default ContaReceberTable;
